import type { Application, Endpoints } from "../api";
import { applicationTypeLabel } from "../presentation";
import { ApplicationStatusBadge } from "./ApplicationStatusBadge";
import { Button } from "./ui/Button";
import { CopyField } from "./ui/CopyField";

interface QuickstartViewProps {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly endpoints: Endpoints | null;
  readonly onSelect: (application: Application) => void;
  readonly onManageApplications: () => void;
}

export function QuickstartView({ application, applications, endpoints, onSelect, onManageApplications }: QuickstartViewProps) {
  if (application === null || endpoints === null) {
    return <section className="empty-page"><strong>请先创建一个应用</strong><p>完成应用注册后，平台会生成对应的接入参数和示例代码。</p><Button variant="primary" onClick={onManageApplications}>前往应用管理</Button></section>;
  }
  const native = application.appType === "native";
  const snippet = native
    ? `import { NativeImClient } from "@sonox/im-sdk";\n\nconst im = new NativeImClient({\n  appKey: "${application.appKey}",\n  userId,\n  userSig, // 由业务服务端签发\n  deviceId: installationDeviceId,\n  platform: "ios",\n});\n\nawait im.login();\nawait im.connect();`
    : `const im = new SonoxImClient({\n  apiBaseUrl: "${endpoints.api}",\n  deviceId: installationDeviceId,\n  getAccessToken: () => oidc.getAccessToken(),\n});\n\nawait im.initialize();\nawait im.connect();`;

  return (
    <div className="quickstart-page">
      <section className="page-heading compact-heading">
        <div><p className="eyebrow">QUICK START</p><h2>接入指南</h2><p>选择应用并按步骤连接 Sonox IM。</p></div>
        <label className="application-select"><span>当前应用</span><select value={application.appId} onChange={(event) => {
          const next = applications.find((item) => item.appId === event.currentTarget.value);
          if (next !== undefined) onSelect(next);
        }}>{applications.map((item) => <option key={item.appId} value={item.appId}>{item.name}</option>)}</select></label>
      </section>
      <section className="quickstart-summary"><div><strong>{application.name}</strong><span>{applicationTypeLabel(application.appType)}</span></div><ApplicationStatusBadge status={application.status} /></section>
      <div className="quickstart-layout">
        <article className="surface-card onboarding-steps">
          <header><span>01—04</span><h3>四步完成接入</h3></header>
          {[
            [native ? "服务端签发 UserSig" : "配置 OIDC", native ? `AppSecret 仅保存在业务服务端，使用 AppKey ${application.appKey}` : `Issuer ${endpoints.issuer}`],
            ["初始化账号", native ? "业务登录后下发 userID + UserSig，再调用 login()" : "登录后生成稳定 deviceId，调用 initialize()"],
            ["建立实时连接", "SDK 获取一次性 Ticket 并连接 WebSocket"],
            ["同步与发送", "按 messageId 去重，断线后调用 Sync 补齐消息"],
          ].map(([title, description], index) => <div className="onboarding-step" key={title}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{title}</strong><p>{description}</p></div></div>)}
        </article>
        <article className="surface-card code-panel">
          <header><div><p className="eyebrow">TYPESCRIPT</p><h3>{native ? "Native SDK" : "Web SDK"}</h3></div><CopyCode value={snippet} /></header>
          <pre><code>{snippet}</code></pre>
          <div className="copy-field-grid"><CopyField label={native ? "AppKey" : "OIDC Issuer"} value={native ? application.appKey : endpoints.issuer} /><CopyField label="REST API" value={endpoints.api} /><CopyField label="WebSocket" value={endpoints.webSocket} /></div>
        </article>
      </div>
    </div>
  );
}

function CopyCode({ value }: { readonly value: string }) {
  return <Button leadingIcon="copy" size="small" variant="ghost" onClick={() => { void navigator.clipboard.writeText(value); }}>复制代码</Button>;
}
